/**
 * Image Proxy Utility
 * Fixes CORS issues with external images by using proxy services
 */

/**
 * Supabase edge function proxy (preferred - server-side, no CORS issues)
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const SUPABASE_PROXY = SUPABASE_URL 
  ? `${SUPABASE_URL}/functions/v1/image-proxy?url=`
  : null;

/**
 * CORS proxy services (free/public options - fallback)
 * These proxy images through their servers to bypass CORS
 * Order matters - first one is tried first
 */
const CORS_PROXIES = [
  'https://corsproxy.io/?', // Alternative proxy
];

/**
 * Check if URL is blocked by CORS
 * Common domains that block CORS:
 */
const CORS_BLOCKED_DOMAINS = [
  'books.google.com',
  'googleapis.com',
  'gstatic.com', // Google static content
  'kotaku.com',
  'hbr.org',
  'newsapi.org',
  'cdn.kotaku.com',
  // S3 buckets (often block CORS)
  's3.amazonaws.com',
  's3.us-east-1.amazonaws.com',
  's3.us-west-1.amazonaws.com',
  's3.us-west-2.amazonaws.com',
  'gettranscribe.s3',
  // Any S3 URL pattern
  '.s3.',
];

/**
 * Domains that typically allow CORS (no proxy needed)
 */
const CORS_ALLOWED_DOMAINS = [
  'img.youtube.com',
  'image.tmdb.org',
  'covers.openlibrary.org',
  'supabase.co',
  'supabase.in',
  // Amazon image CDNs
  'images-na.ssl-images-amazon.com',
  'images-amazon.com',
  'm.media-amazon.com',
];

/**
 * Proxy an image URL to bypass CORS
 * @param {string} url - Original image URL
 * @param {boolean} forceProxy - Force proxy even if domain might allow CORS
 * @returns {string} Proxied URL or original if proxy not needed
 */
export function proxyImageUrl(url, forceProxy = false) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Clean URL
  let cleanUrl = url.trim();
  
  // CRITICAL: NEVER proxy Supabase Storage URLs (wastes egress, doubles bandwidth)
  // Check for our project host or any Supabase storage URL
  const PROJECT_HOST = SUPABASE_URL ? new URL(SUPABASE_URL).host : '';
  try {
    const urlObj = new URL(cleanUrl);
    const host = urlObj.host;
    
    // Never proxy our own Supabase project URLs
    if (host === PROJECT_HOST || host.includes('supabase.co') || host.includes('supabase.in')) {
      return cleanUrl; // Use direct URL - no proxy needed for Supabase
    }
  } catch {
    // Invalid URL, continue with other checks
  }
  
  // Check if it's already a proxied URL
  if (CORS_PROXIES.some(proxy => cleanUrl.includes(proxy))) {
    return cleanUrl; // Already proxied
  }
  
  // Check if it's a data URL or relative URL - no proxy needed
  if (cleanUrl.startsWith('data:') || cleanUrl.startsWith('/')) {
    return cleanUrl;
  }
  
  // Check if domain is known to allow CORS
  const allowsCORS = CORS_ALLOWED_DOMAINS.some(domain => 
    cleanUrl.includes(domain)
  );
  
  if (allowsCORS && !forceProxy) {
    return cleanUrl; // No proxy needed
  }
  
  // Check if URL is from a known CORS-blocked domain
  const isBlockedDomain = CORS_BLOCKED_DOMAINS.some(domain => 
    cleanUrl.includes(domain)
  );
  
  // Check if it's an S3 URL (common pattern: bucket.s3.region.amazonaws.com)
  const isS3Url = cleanUrl.includes('.s3.') || cleanUrl.includes('s3.amazonaws.com');
  
  // Proxy if: forced, blocked domain, or S3 URL (S3 buckets often block CORS)
  // OR if it's an external URL not in the allowed list (conservative approach)
  const isExternalUrl = cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://');
  const needsProxy = forceProxy || isBlockedDomain || isS3Url || 
    (isExternalUrl && !allowsCORS); // Proxy external URLs by default unless known to allow CORS

  // If proxy not needed, return original
  if (!needsProxy) {
    return cleanUrl;
  }

  try {
    // Prefer Supabase edge function proxy (server-side, no CORS)
    if (SUPABASE_PROXY) {
      const encodedUrl = encodeURIComponent(cleanUrl);
      // Add anon key as query param for public access (Supabase allows this)
      const proxiedUrl = SUPABASE_ANON_KEY 
        ? `${SUPABASE_PROXY}${encodedUrl}&apikey=${SUPABASE_ANON_KEY}`
        : `${SUPABASE_PROXY}${encodedUrl}`;
      
      // Debug logging removed - was spamming console
      
      // Warn if proxy URL seems malformed
      if (!proxiedUrl.includes('/functions/v1/image-proxy')) {
        console.warn('⚠️ Proxy URL may be malformed:', proxiedUrl);
      }
      
      return proxiedUrl;
    }
    
    // Fallback to public proxy
    if (CORS_PROXIES.length > 0) {
      const proxy = CORS_PROXIES[0];
      const encodedUrl = encodeURIComponent(cleanUrl);
      return `${proxy}${encodedUrl}`;
    }
    
    // If no proxy available, return original (will likely fail but better than nothing)
    return cleanUrl;
  } catch (error) {
    console.warn('Error proxying image URL:', error);
    return cleanUrl; // Return original on error
  }
}

/**
 * Get image URL with automatic CORS proxy if needed
 * @param {Object} item - Item object with image/thumbnail_url
 * @returns {string|null} Image URL (proxied if needed)
 */
export function getImageUrl(item) {
  if (!item) return null;

  // Try different image sources
  const imageUrl = item.image || item.thumbnail_url || item.thumbnail || 
                   item.meta?.thumbnail_url || item.meta?.image || null;

  if (!imageUrl) return null;

  // IMPORTANT: CORS does NOT block <img> display; it blocks programmatic pixel access.
  // We prefer direct URLs when possible to avoid brittle proxy behavior.
  // GetTranscribe thumbnails are frequently public but can fail via server-side proxy fetches.
  if (
    imageUrl.includes('gettranscribe.s3') ||
    imageUrl.includes('.s3.') ||
    imageUrl.includes('s3.amazonaws.com')
  ) {
    return imageUrl;
  }

  // Check if it's a data URL (base64) - no proxy needed
  if (imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  // Check if it's a relative URL - no proxy needed
  if (imageUrl.startsWith('/')) {
    return imageUrl;
  }
  
  // Check if it's a Supabase storage URL (these should work without proxy)
  // Also handle storage paths that need to be converted to public URLs
  if (imageUrl.includes('supabase.co/storage') || imageUrl.includes('supabase.in/storage')) {
    return imageUrl;
  }
  
  // If it's a storage path (not a full URL), convert it to a public URL
  if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && !imageUrl.startsWith('/')) {
    // It's likely a storage path like "items/{itemId}.{ext}" or "{itemId}.{ext}"
    const supabaseUrl = SUPABASE_URL;
    if (supabaseUrl) {
      // Remove any leading "items/" prefix if present
      const path = imageUrl.startsWith('items/') ? imageUrl.replace('items/', '') : imageUrl;
      return `${supabaseUrl}/storage/v1/object/public/items/${path}`;
    }
  }

  // Check if domain is known to allow CORS (no proxy needed)
  const allowsCORS = CORS_ALLOWED_DOMAINS.some(domain => 
    imageUrl.includes(domain)
  );
  
  if (allowsCORS) {
    return imageUrl; // Use direct URL - no proxy needed
  }

  // Proxy external URLs that may have CORS issues
  return proxyImageUrl(imageUrl, false); // Let proxyImageUrl decide if proxy is needed
}

/**
 * Create an img element with CORS handling
 * @param {string} src - Image source URL
 * @param {Object} options - Additional options
 * @returns {HTMLImageElement} Image element
 */
export function createImageWithProxy(src, options = {}) {
  const img = new Image();
  const proxiedUrl = proxyImageUrl(src);
  
  img.crossOrigin = 'anonymous';
  img.src = proxiedUrl;
  
  if (options.onLoad) {
    img.onload = options.onLoad;
  }
  
  if (options.onError) {
    img.onerror = () => {
      // Try original URL if proxy fails
      if (proxiedUrl !== src) {
        img.src = src;
        img.onerror = options.onError;
      } else {
        options.onError();
      }
    };
  }
  
  return img;
}

